import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { EVENT_KIND, RunEventBlock } from './RunEventBlock';
import type { RunEvent } from './api';

type Filter = 'all' | 'llm_msg' | 'tool_call' | 'tool_result' | 'sign' | 'error' | 'final';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'llm_msg', label: 'LLM' },
  { value: 'tool_call', label: 'Tools' },
  { value: 'sign', label: 'Sign' },
  { value: 'error', label: 'Errors' },
];

type Props = {
  events: RunEvent[];
  loading: boolean;
  liveStreaming: boolean;
};

export function RunTraceList({ events, loading, liveStreaming }: Props) {
  const [filter, setFilter] = useState<Filter>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const endRef = useRef<HTMLDivElement | null>(null);

  const filtered = useMemo(() => {
    if (filter === 'all') return events;
    if (filter === 'tool_call') {
      return events.filter((e) => e.kind === 'tool_call' || e.kind === 'tool_result');
    }
    return events.filter((e) => e.kind === filter);
  }, [events, filter]);

  // Auto-scroll while the run is producing events. The user can pause it
  // by toggling the checkbox if they're trying to read something earlier.
  useEffect(() => {
    if (!autoScroll || !liveStreaming) return;
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [filtered, autoScroll, liveStreaming]);

  return (
    <Card>
      <CardHeader
        title="Trace"
        description="Live updates every 3 seconds while the run is in flight."
      />
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <FilterPill
              key={f.value}
              active={filter === f.value}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </FilterPill>
          ))}
          <span className="font-mono text-[11px] text-ink-tertiary">
            {filtered.length} / {events.length}
          </span>
          {liveStreaming && (
            <label className="ml-auto flex items-center gap-2 font-mono text-[11px] text-ink-secondary">
              <input
                type="checkbox"
                checked={autoScroll}
                onChange={(e) => setAutoScroll(e.target.checked)}
                className="accent-bit2me"
              />
              auto-scroll
            </label>
          )}
        </div>

        <Legend />

        {loading && events.length === 0 ? (
          <p className="text-sm text-ink-tertiary">Loading trace…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-ink-tertiary">No events match this filter.</p>
        ) : (
          <div className="space-y-2">
            {filtered.map((e, i) => (
              <RunEventBlock key={`${e.ord}-${i}`} event={e} />
            ))}
            <div ref={endRef} />
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-line-soft bg-surface-soft/30 px-3 py-1.5">
      {Object.entries(EVENT_KIND).map(([k, meta]) => (
        <span key={k} className="flex items-center gap-1.5 font-mono text-[10px] text-ink-tertiary">
          <span className={`h-2 w-2 rounded-sm ${meta.bar}`} />
          {meta.label}
        </span>
      ))}
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-eyebrow transition-colors ${
        active
          ? 'border-bit2me bg-bit2me/10 text-bit2me'
          : 'border-line bg-surface-white text-ink-secondary hover:border-ink-secondary'
      }`}
    >
      {children}
    </button>
  );
}
